/**
 * ETC Ledger — Flat, precise ETC entry management for third-party pages.
 *
 * Instead of silently accumulating multiple DB rows into a single breadcrumb key,
 * this module keeps every individual Forecast_User_Data row as a discrete entry.
 * The tree builder still gets a sum per breadcrumb, but the UI can access the
 * individual entries for edit/delete via popover.
 *
 * Key differences from the old setUpUserObject approach:
 *   1. No silent accumulation — every DB row is an individual entry
 *   2. Each entry has explicit column values (Phase, Vendor, PO, etc.)
 *   3. Level is determined by Equipment_Tier first, then column depth
 *   4. Returns both a summed userEtcMap (for buildColumnTree) and an entries array per breadcrumb (for popover)
 */

import { pNum } from '../db/company';

const norm = v => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    return (s === 'undefined' || s === 'null') ? '' : s;
};

/**
 * Build a breadcrumb key from explicit column values in drill order.
 * E.g. drillColumns = ['Phase_Code','Supplier_Name','PO_Number','Line_ID']
 *      colValues = { Phase_Code: '100102', Supplier_Name: 'Acme' }
 *      depth = 2  →  "^100102^Acme"
 */
const breadcrumbFromColumns = (colValues, drillColumns, depth) => {
    let bc = '';
    for (let i = 0; i < depth; i++) {
        bc += '^' + norm(colValues[drillColumns[i]] || '');
    }
    return bc;
};

/**
 * Determine the depth (number of drill levels) a DB row belongs to.
 *
 * Always returns max depth (drillColumns.length) so entries cascade as far as
 * possible down the tree. Missing downstream column values become empty-string
 * keys (rendered as "Unknown <Level>") rather than leaving the entry stranded
 * at a shallow level where it only shows in the phase total.
 *
 * Equipment_Tier is ignored — we let the actual column data decide placement.
 */
const resolveDepth = (_row, drillColumns) => drillColumns.length;

/**
 * Process raw Forecast_User_Data rows into a structured ETC ledger.
 *
 * @param {Array}  rawUserData   – all rows from Forecast_User_Data for the job/version
 * @param {string} tab           – 'Materials', 'Rentals', etc.
 * @param {Array}  drillColumns  – current drill order, e.g. ['Phase_Code','Supplier_Name','PO_Number','Line_ID']
 * @param {Object} disciplineMap – phaseCode → discipline (from Productivity Tracker)
 * @returns {{
 *   userEtcMap: Object,       – breadcrumb → { etc, costType, notes, description, title, entries, found }  (for buildColumnTree)
 *   entriesByBreadcrumb: Object, – breadcrumb → Entry[]  (individual DB rows, for popover)
 *   allEntries: Array,        – flat array of all entries (for ValidationArea)
 *   dayZeroBudget: Object,    – phase → budget amount
 *   totalBudget: number,
 *   costCodeSeeds: Object,    – phase → seed row for tree (ensures phase nodes appear)
 * }}
 */
export const buildEtcLedger = (rawUserData, tab, drillColumns, disciplineMap = {}) => {
    if (!rawUserData || !rawUserData.length) {
        return { userEtcMap: {}, entriesByBreadcrumb: {}, allEntries: [], dayZeroBudget: {}, totalBudget: 0, costCodeSeeds: {}, costTypeBudget: {}, budgetByCtPhase: {}, budgetByPhaseCt: {}, budgetByResource: {}, budgetByResourceCt: {} };
    }

    const entriesByBreadcrumb = {};   // breadcrumb → Entry[]
    const allEntries = [];
    const dayZeroBudget = {};
    const costTypeBudget = {};          // costType → total budget
    const budgetByCtPhase = {};         // costType → { phase → budget }
    const budgetByPhaseCt = {};         // phase → { costType → budget }
    const budgetByResource = {};        // resource → total budget
    const budgetByResourceCt = {};      // resource → { costType → budget }
    const coPieces = {};
    const costCodeSeeds = {};
    let totalBudget = 0;

    rawUserData.forEach(row => {
        // ── Day Zero Budget ──
        if (row.Forecast_Tab === 'Day Zero Budget') {
            const phase = norm(row.Cost_Code);
            const ct = norm(row.Cost_Type);
            const res = norm(row.Resource_Code);
            // Exclude contingency (101101) and revenue (4xxxx) — matches sidebar logic.
            // A blank phase is allowed: budget rows saved without a cost code group
            // under the blank phase key ('' → "Unknown Phase" node) so the page
            // total still reconciles with the side menu.
            if (phase !== '101101' && ct && ct[0] !== '4') {
                const amt = pNum(row.Value_Amount);
                dayZeroBudget[phase] = (dayZeroBudget[phase] || 0) + amt;
                totalBudget += amt;
                costTypeBudget[ct] = (costTypeBudget[ct] || 0) + amt;
                budgetByCtPhase[ct] = budgetByCtPhase[ct] || {};
                budgetByCtPhase[ct][phase] = (budgetByCtPhase[ct][phase] || 0) + amt;
                budgetByPhaseCt[phase] = budgetByPhaseCt[phase] || {};
                budgetByPhaseCt[phase][ct] = (budgetByPhaseCt[phase][ct] || 0) + amt;
                if (res) {
                    budgetByResource[res] = (budgetByResource[res] || 0) + amt;
                    budgetByResourceCt[res] = budgetByResourceCt[res] || {};
                    budgetByResourceCt[res][ct] = (budgetByResourceCt[res][ct] || 0) + amt;
                }
                costCodeSeeds[phase + '^' + ct] = {
                    BU_ID: row.BU_ID,
                    Cost_Code: phase,
                    Cost_Type: ct,
                    Cost_Type_Code: ct,
                    Phase_Code: phase,
                    Supplier_Name: '',
                    PO_Number: '',
                    rowId: '^' + phase,
                };
            }
            return;
        }

        // ── Change Orders ──
        // Raw DB rows store COs as Cost_Type + Value_Amount (one row per cost type).
        if (row.Forecast_Tab === 'Change Order') {
            if (row.Status === 'Approved') {
                const phase = norm(row.Cost_Code);
                const ct = norm(row.Cost_Type);
                const res = norm(row.Resource_Code);
                // Exclude contingency (101101) and revenue (4xxxx) — matches sidebar
                // logic. Blank phase allowed, same as Day Zero above.
                if (phase !== '101101' && ct && ct[0] !== '4') {
                    const amt = pNum(row.Value_Amount);
                    if (amt) {
                        dayZeroBudget[phase] = (dayZeroBudget[phase] || 0) + amt;
                        totalBudget += amt;
                        costTypeBudget[ct] = (costTypeBudget[ct] || 0) + amt;
                        budgetByCtPhase[ct] = budgetByCtPhase[ct] || {};
                        budgetByCtPhase[ct][phase] = (budgetByCtPhase[ct][phase] || 0) + amt;
                        budgetByPhaseCt[phase] = budgetByPhaseCt[phase] || {};
                        budgetByPhaseCt[phase][ct] = (budgetByPhaseCt[phase][ct] || 0) + amt;
                        if (res) {
                            budgetByResource[res] = (budgetByResource[res] || 0) + amt;
                            budgetByResourceCt[res] = budgetByResourceCt[res] || {};
                            budgetByResourceCt[res][ct] = (budgetByResourceCt[res][ct] || 0) + amt;
                        }
                    }
                }
                // Ensure CO phases appear as seed rows — key by phase+ct so a phase
                // with multiple cost-type budgets produces a seed for each cost type.
                if (phase !== '101101' && ct && ct[0] !== '4' && !costCodeSeeds[phase + '^' + ct]) {
                    costCodeSeeds[phase + '^' + ct] = {
                        BU_ID: row.BU_ID,
                        Cost_Code: phase,
                        Cost_Type: ct,
                        Cost_Type_Code: ct,
                        Phase_Code: phase,
                        Supplier_Name: '',
                        PO_Number: '',
                        rowId: '^' + phase,
                    };
                }
            }
            return;
        }

        // ── ETC entries for this tab ──
        // 'All Third Party' sees ETC from all third-party tabs
        const thirdPartyTabs = ['Materials', 'Rentals', 'Subcontractors', 'Other', 'All Third Party'];
        if (tab === 'All Third Party') {
            if (!thirdPartyTabs.includes(row.Forecast_Tab)) return;
        } else {
            if (row.Forecast_Tab !== tab) return;
        }

        // Extract explicit column values from DB row
        const colValues = {
            Phase_Code: norm(row.Cost_Code),
            Supplier_Name: norm(row.Supplier_Name),
            PO_Number: norm(row.PO_Number),
            Cost_Type_Code: norm(row.Cost_Type),
            Discipline: norm(row.Cost_Code) ? (disciplineMap[norm(row.Cost_Code)] || '') : '',
            Resource_Code: norm(row.Resource_Code || row.Purchasing_Reporting_Code_4 || row.Purchasing_Reporting_Code_1 || row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || ''),
            Line_ID: '',
        };

        // Always compute breadcrumb from column values for drill-order independence.
        // This ensures breadcrumbs stay correct when the user reorders pivots.
        const depth = resolveDepth(row, drillColumns);
        const breadcrumb = breadcrumbFromColumns(colValues, drillColumns, depth);

        const entry = {
            guid: row.Forecast_User_Data_GUID,
            breadcrumb,
            depth,
            amount: pNum(row.Value_Amount),
            notes: norm(row.Notes),
            description: norm(row.Description),
            title: norm(row.Row_Label),
            level: norm(row.Equipment_Tier),
            costType: norm(row.Cost_Type),
            // Preserve original column values for precise re-save
            Phase_Code: colValues.Phase_Code,
            Supplier_Name: colValues.Supplier_Name,
            PO_Number: colValues.PO_Number,
            Cost_Type_Code: colValues.Cost_Type_Code,
            Discipline: colValues.Discipline,
            Resource_Code: colValues.Resource_Code,
            // Original DB row reference (for GUID-based updates)
            _row: row,
        };

        allEntries.push(entry);

        if (!entriesByBreadcrumb[breadcrumb]) {
            entriesByBreadcrumb[breadcrumb] = [];
        }
        entriesByBreadcrumb[breadcrumb].push(entry);
    });

    // Build the userEtcMap for buildColumnTree — sum per breadcrumb, but keep entries array
    const userEtcMap = {};
    Object.entries(entriesByBreadcrumb).forEach(([bc, entries]) => {
        const totalEtc = entries.reduce((sum, e) => sum + e.amount, 0);
        const first = entries[0];
        userEtcMap[bc] = {
            etc: totalEtc,
            costType: first.costType,
            notes: first.notes,
            description: first.description,
            title: first.title,
            rowId: first._row?.Forecast_Row_ID,
            entries,                   // ← the key addition: individual entries for popover
            found: false,              // will be set to true by buildColumnTree when matched
            // Column values for tree seed rows
            Phase_Code: first.Phase_Code,
            Supplier_Name: first.Supplier_Name,
            PO_Number: first.PO_Number,
            Cost_Type_Code: first.Cost_Type_Code,
            Value_Amount: totalEtc,
            Forecast_Row_ID: first._row?.Forecast_Row_ID,
        };
    });

    // Resource budget seed rows so budget-only resources appear in the tree
    // even when there are no OC/CH/ETC rows for them. Each seed is a ghost
    // row with Phase_Code + Cost_Type_Code + Resource_Code set but no
    // Value_Amount, so the tree builder creates the node and the resolver
    // fills in budget. Built directly from budget rows to preserve phase.
    const resourceCtSeeds = [];
    const resSeedSeen = new Set();
    rawUserData.forEach(row => {
        const isBudget = row.Forecast_Tab === 'Day Zero Budget' || (row.Forecast_Tab === 'Change Order' && row.Status === 'Approved');
        if (!isBudget) return;
        const phase = norm(row.Cost_Code);
        const ct = norm(row.Cost_Type);
        const res = norm(row.Resource_Code);
        if (!res || !ct || phase === '101101' || ct[0] === '4') return;
        const key = phase + '^' + ct + '^' + res;
        if (resSeedSeen.has(key)) return;
        resSeedSeen.add(key);
        resourceCtSeeds.push({
            Cost_Type: ct,
            Cost_Type_Code: ct,
            Resource_Code: res,
            Phase_Code: phase,
            Cost_Code: phase,
            Supplier_Name: '',
            PO_Number: '',
            Line_ID: '',
            Discipline: '',
            rowId: '^seed^' + res + '^' + ct + '^' + phase,
            _isResourceSeed: true,
        });
    });

    return {
        userEtcMap,
        entriesByBreadcrumb,
        allEntries,
        dayZeroBudget,
        costTypeBudget,
        budgetByCtPhase,
        budgetByPhaseCt,
        budgetByResource,
        budgetByResourceCt,
        totalBudget,
        costCodeSeeds,
        resourceCtSeeds,
    };
};

/**
 * Re-key userEtcMap for user-data rows whose column values may have been
 * enriched after the ledger ran (e.g. Resource_Code inferred from cost history).
 *
 * Also rewrites each matching row's Forecast_Row_ID to the fresh breadcrumb so
 * buildColumnTree's `row.Forecast_Row_ID === breadcrumb` check succeeds.
 *
 * @param {Array}  rows         – the same rows array that will be passed to buildColumnTree
 * @param {Object} userEtcMap   – the ledger-built userEtcMap
 * @param {Array}  drillPath    – the drillPath the tree will use (minus PO_Detail)
 * @returns {Object} a new userEtcMap re-keyed to match tree-computed breadcrumbs
 */
export const rekeyEtcMapByColumns = (rows, userEtcMap, drillPath) => {
    const out = { ...userEtcMap };
    rows.forEach(row => {
        const id = row.Forecast_Row_ID;
        if (!id || id.includes('^~u') || !row.Forecast_Tab) return;
        const orig = userEtcMap[id];
        if (!orig) return;
        let newBc = '';
        for (const col of drillPath) newBc += '^' + norm(row[col]);
        if (newBc === id) return;
        // Prefer the fresh breadcrumb; drop the stale one to avoid double-counting.
        out[newBc] = orig;
        delete out[id];
        row.Forecast_Row_ID = newBc;
    });
    return out;
};

/**
 * Get the level label for a user data row (for ValidationArea).
 * Prefers Equipment_Tier, then falls back to breadcrumb parsing / column depth.
 */
export const getEntryLevel = (entry, drillColumns) => {
    if (entry.level) return entry.level;
    if (entry.breadcrumb?.includes('^~u')) return 'User Row';
    if (entry.breadcrumb?.includes('t^')) return 'Top Level';
    // Fallback: use depth
    const colToLabel = {
        'Phase_Code': 'Phase',
        'Supplier_Name': 'Vendor',
        'PO_Number': 'PO',
        'Line_ID': 'Line',
        'Cost_Type_Code': 'Cost Type',
        'Discipline': 'Discipline',
        'Resource_Code': 'Resource',
    };
    const colAtDepth = drillColumns[Math.max(0, (entry.depth || 1) - 1)];
    return colToLabel[colAtDepth] || 'Phase';
};
